import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { withTranslation } from "react-i18next";
import * as moment from "moment";
import { generateWeek, encodeId } from "utils";
import constants from "../../../../../../../constants.js";
import HolidayImage from "assets/images/early_on.svg";
import Loader from "components/Loader";
import toast from "react-hot-toast";
import Empty from "components/Empty";
import { filteringMethodForEventShiftOthers } from "accountOwner/pages/Scheduler/scheduler.function";
import {
  renderOfficeNameOrEnterprise,
  setterCustomSchduler,
  setterPublishOffices,
  matchedBothPatternValues,
  matchedBothDatesValues,
} from "accountOwner/pages/AddEvent/addEvents.functions";
import { formatBusySlotTimeValue } from "./busySlotTime.helpers";
import { addEditEventsConst } from "accountOwner/pages/AddEvent/addEvents.constants";
import EditOptionsModal, {
  editTypesVariables,
} from "accountOwner/pages/EditEvent/EditOptionsModal";
import styles from "../../../../OfficeSchedulerTab/SubTabs/StaffAvailabilityTab/StaffAvailabilityTab.module.scss";
import schedulerStyles from "../../../../Scheduler.module.scss";
import { useHistory, useLocation } from "react-router-dom";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import {
  updateBusySlot,
  useCreateUpdateSchedulerEventMutation,
  getEventListDetails,
  getBusySlot,
} from "repositories/scheduler-repository";
import { useQueryClient } from "react-query";
import { getOfficeDetail } from "repositories/office-repository";
import MoveToApp from "accountOwner/pages/Scheduler/components/Modals/MoveToApp";
import { ThreeDots } from "react-bootstrap-icons";
import {
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
} from "reactstrap";
import SelectOfficeModal from "accountOwner/pages/Scheduler/components/Modals/SelectOfficeModal";
import { showTime } from "accountOwner/pages/Scheduler/OfficeSchedulerTab/SubTabs/EventsShiftsTab";

function WeeklyListView({
  t,
  currentDate,
  selectedOwnerId,
  currentUserId,
  agendaTypes = [],
  viewSettingFilter = [],
  isEnterpriseFilterActive = false,
  isFreeStotLoading = false,
  canShowOfficeName = true,
  onDateChange,
  agenda,
  isLoading,
  isFetching,
  error,
  setDateRange,
  isStaff,
}) {
  const [showPopup, setShowPopup] = useState(false);
  const [weekData, setWeekData] = useState([]);
  const [agendaFilter, setAgendaFilter] = useState([]);
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPreviewPosition, setDragPreviewPosition] = useState(null);
  const [editModalConfirmation, setEditModalConfirmation] = useState(false);
  const [pendingDragUpdate, setPendingDragUpdate] = useState(null);
  const [isDragDropLoading, setIsDragDropLoading] = useState(false);
  const [isModal, setIsModal] = useState({ open: false });
  const [showOfficeSelectModal, setShowOfficeSelectModal] = useState(false);
  const [SelectedDateForLeave, setSelectedDateForLeave] = useState(null);
  const [dynamicHeight, setDynamicHeight] = useState("calc(100vh - 200px)");
  const history = useHistory();
  const { profile, isOwner, dataAccessValues, effectiveOwnerIdString } =
    useAccountOwnerCommon();
  const createSchedulerEventMutation = useCreateUpdateSchedulerEventMutation();
  const queryClient = useQueryClient();
  const draggedEventDateRef = useRef(null);

  const canEditAndViewOthersData = useCallback(
    (officeId) => {
      if (isStaff) {
        return (
          dataAccessValues?.officeList?.some(
            (res) => res.officeId === officeId && res.isAdmin,
          ) || false
        );
      }
      return isOwner;
    },
    [isStaff, isOwner, dataAccessValues?.officeList],
  );

  const isPastDate = (date) => {
    if (!date) return false;
    return moment(date, "YYYY-MM-DDTHH:mm:ss").isBefore(moment(), "day");
  };

  const containerRef = useRef(null);
  const popupRef = useRef(null);
  const highlightRef = useRef(null);
  const { search } = useLocation();

  // Get date from URL search params for highlighting
  const dateMatchForHighlight = useMemo(() => {
    const searchValue = new URLSearchParams(search).get(showTime);
    if (searchValue?.includes("NaN") || !search) {
      return null;
    }
    return new Date(+searchValue);
  }, [search]);

  // --- Dynamic Height Calculation Logic Start ---
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        // Calculate available space: Window Height - Top Position - Bottom Buffer
        // The scroll listener ensures rect.top is updated as user scrolls, adjusting height dynamically
        const availableHeight = window.innerHeight - rect.top - 20;

        // Ensure a minimum height so it doesn't collapse
        setDynamicHeight(`${Math.max(300, availableHeight)}px`);
      }
    };

    // Calculate initially
    handleResize();

    // Add event listeners for BOTH resize and scroll
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleResize);

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleResize);
    };
  }, [agendaFilter, weekData]);
  // --- Dynamic Height Calculation Logic End ---

  // Generate week dates (Sunday to Saturday)
  useEffect(() => {
    if (currentDate) {
      const selectedStartWeek = moment(currentDate).startOf("week"); // Sunday
      const selectedEndWeek = moment(currentDate).endOf("week"); // Saturday
      const currentweekData = generateWeek(selectedStartWeek, selectedEndWeek);
      const range = {
        startDate: moment(selectedStartWeek).format("YYYY-MM-DD"),
        endDate: moment(selectedEndWeek).format("YYYY-MM-DD"),
      };
      if (setDateRange) {
        setDateRange(range);
      }
      setWeekData(currentweekData);
    }
  }, [currentDate, setDateRange]);

  // Filter agenda by events
  const filterByEvents = useCallback(
    (agendaData) => {
      if (!agendaData) return [];
      const updatedAgenda = agendaData.map((item) => {
        return filteringMethodForEventShiftOthers(
          agendaTypes,
          item,
          currentUserId,
          isEnterpriseFilterActive,
        );
      });
      return updatedAgenda.filter((item) => item !== null);
    },
    [agendaTypes, currentUserId, isEnterpriseFilterActive],
  );

  // Process agenda data - agenda is already filtered by parent and grouped by date
  useEffect(() => {
    if (!isLoading) {
      if (agenda && Array.isArray(agenda)) {
        // Agenda structure is already grouped by date (like AgendaWeeklyView expects)
        // Each item has: requestedDate, appointments, leaves, schedulerBusySlots, schedulerEvents, appointmentSlots, holidayList
        setAgendaFilter(agenda);
      } else {
        setAgendaFilter([]);
      }
    }
  }, [isLoading, agenda]);

  useEffect(() => {
    if (!isLoading && !isFetching && error?.message) {
      toast.error(error.message);
    }
  }, [error, isLoading, isFetching]);

  // Transform agenda data (already grouped by date) into format needed for WeeklyListView
  const groupedAgendaByDate = useMemo(() => {
    if (!agendaFilter?.length || !weekData?.length) return [];

    // Get week date range for filtering
    const weekStart = weekData[0]?.date
      ? moment(weekData[0].date).startOf("day")
      : null;
    const weekEnd = weekData[weekData.length - 1]?.date
      ? moment(weekData[weekData.length - 1].date).endOf("day")
      : null;

    if (!weekStart || !weekEnd) return [];

    // Agenda is already grouped by date (like AgendaWeeklyView structure)
    // Each item has: requestedDate, appointments, leaves, schedulerBusySlots, schedulerEvents, appointmentSlots, holidayList
    return agendaFilter
      .filter((dateItem) => {
        // Only include dates within the current week
        if (!dateItem.requestedDate) return false;
        const dateMoment = moment(dateItem.requestedDate);
        return (
          dateMoment.isSameOrAfter(weekStart, "day") &&
          dateMoment.isSameOrBefore(weekEnd, "day")
        );
      })
      .map((dateItem) => {
        // Transform to the format expected by WeeklyListView
        const {
          requestedDate,
          appointments = [],
          leaves = [],
          schedulerBusySlots = [],
          schedulerEvents = [],
          appointmentSlots = [],
          holidayList = [],
        } = dateItem;

        return {
          requestedDate,
          holidayList,
          // Create a single "user" entry since it's My Schedule (current user only)
          officeSchedulerList: [
            {
              user: {
                id: currentUserId,
                fullName: profile?.fullName || "",
                profilePic: profile?.profilePic || null,
                officeNames: [],
              },
              schedulerEvents: Array.isArray(schedulerEvents)
                ? schedulerEvents
                : [],
              schedulerBusySlots: Array.isArray(schedulerBusySlots)
                ? schedulerBusySlots
                : [],
              appointments: Array.isArray(appointments) ? appointments : [],
              leaves: Array.isArray(leaves) ? leaves : [],
              appointmentSlots: Array.isArray(appointmentSlots)
                ? appointmentSlots
                : [],
            },
          ],
        };
      });
  }, [agendaFilter, currentUserId, profile, weekData]);

  // Generate days from week data
  const days = useMemo(() => {
    return weekData.map((day) => ({
      date: day.mDate,
      momentDate: day.date,
    }));
  }, [weekData]);

  // Generate hours (12 AM to 11 PM - 24 hours)
  const hours = useMemo(() => {
    const hourList = [];
    for (let i = 0; i <= 23; i++) {
      const hour = moment().hour(i).minute(0);
      hourList.push({
        label: hour.format("h A"),
        moment: hour,
        hour: i,
      });
    }
    return hourList;
  }, []);

  // Helper function to get all events from agenda item
  const getAllEvents = useCallback((scheduler) => {
    const events = [];

    // Add scheduler events
    if (scheduler.schedulerEvents) {
      events.push(
        ...scheduler.schedulerEvents.map((event) => ({
          ...event,
          type: "schedulerEvent",
        })),
      );
    }

    // Add appointment slots
    if (scheduler.appointmentSlots) {
      events.push(
        ...scheduler.appointmentSlots.map((slot) => ({
          ...slot,
          type: "appointmentSlot",
          title: slot.reasonForBlock || "Appointment Slot",
        })),
      );
    }

    // Add busy slots
    if (scheduler.schedulerBusySlots) {
      events.push(
        ...scheduler.schedulerBusySlots.map((slot) => ({
          ...slot,
          type: "busySlot",
          isBusySlot: true,
          agendaType: slot.agendaType || constants.agendaType.BUSY_SLOT,
          title: slot.title || "Busy",
        })),
      );
    }

    // Add appointments
    if (scheduler.appointments) {
      events.push(
        ...scheduler.appointments.map((appointment) => ({
          ...appointment,
          type: "appointment",
          title: appointment.description || "Appointment",
        })),
      );
    }

    // Add leaves
    if (scheduler.leaves) {
      events.push(
        ...scheduler.leaves.map((leave) => ({
          ...leave,
          type: "leave",
          isAllDayEvent: true,
          title: leave.reason || "Leave",
        })),
      );
    }

    return events;
  }, []);

  // Categorize events into all-day and time-based
  const categorizeEvents = useCallback((events) => {
    return {
      allDayEvents: events.filter(
        (event) =>
          event.isAllDayEvent ||
          event.isAllDayBusy ||
          (event.startTime &&
            event.endTime &&
            new Date(event.startTime).toDateString() !==
              new Date(event.endTime).toDateString()),
      ),
      timeEvents: events.filter(
        (event) =>
          !event.isAllDayEvent &&
          !event.isAllDayBusy &&
          event.startTime &&
          event.endTime &&
          new Date(event.startTime).toDateString() ===
            new Date(event.endTime).toDateString(),
      ),
    };
  }, []);

  // Get event colors based on type (matching AgendaWeeklyView logic)
  const getEventColor = useCallback(
    (event) => {
      if (!event || !event.agendaType) {
        return "#c7e9ff";
      }
      const pendingCheck =
        event?.statusId === constants.agendaType.PENDINGLEAVE;

      switch (event.agendaType) {
        case constants.agendaType.LEAVE:
          return pendingCheck ? "#fdeded" : "rgba(113, 213, 185, 0.2)";
        case constants.agendaType.BUSY_SLOT:
        case constants.agendaType.BLOCKED:
          return "#fef3d7";
        case constants.agendaType.EVENT: {
          // Match AgendaWeeklyView logic: createdByMe = default, pending = blue, others = grey
          const createdByMe = currentUserId === event.createdById;
          const status =
            event.eventEmployeeStatus || event.eventRequestToJoinStatus;
          if (createdByMe) {
            return "#f9fcee"; // Default background (same as scheduler-event-box)
          } else if (status === constants.SCHEDULERSTATUS.PENDING) {
            return "#e1f4fd"; // color-blue
          } else {
            return "#f2f3ef"; // color-grey
          }
        }
        case constants.agendaType.APPOINTMENT:
          return "#f6f1fd";
        default:
          return "#c7e9ff";
      }
    },
    [currentUserId],
  );

  const getEventBorderColor = useCallback(
    (event) => {
      if (!event || !event.agendaType) {
        return "#005f99";
      }
      const pendingCheck =
        event?.statusId === constants.agendaType.PENDINGLEAVE;
      switch (event.agendaType) {
        case constants.agendaType.LEAVE:
          return pendingCheck ? "#ff5757" : "#71d5b9";
        case constants.agendaType.BUSY_SLOT:
        case constants.agendaType.BLOCKED:
          return "#f7cb7a";
        case constants.agendaType.EVENT:
          if (event.eventStatus === constants.SCHEDULERSTATUS.PENDING) {
            return "#82d8f8";
          } else if (currentUserId === event.createdById) {
            return "#f7cb7a";
          } else {
            return "#707070";
          }
        case constants.agendaType.APPOINTMENT:
          return "#d8b4f8";
        default:
          return "#005f99";
      }
    },
    [currentUserId],
  );

  // Process events from grouped agenda data
  const { allDayEventsMap, timeEventsByDate } = useMemo(() => {
    const allDayMap = {};
    const timeMap = {};

    groupedAgendaByDate?.forEach((dateObj) => {
      const { requestedDate, officeSchedulerList, holidayList } = dateObj;
      const dateKey = moment(requestedDate).format("MMM DD, ddd");

      if (!allDayMap[dateKey]) {
        allDayMap[dateKey] = {};
      }
      if (!timeMap[dateKey]) {
        timeMap[dateKey] = [];
      }

      officeSchedulerList?.forEach((scheduler) => {
        const allEvents = getAllEvents(scheduler);
        const { allDayEvents, timeEvents } = categorizeEvents(allEvents);

        // Process all-day events
        allDayEvents.forEach((item) => {
          const userId = scheduler.user?.id;
          if (!allDayMap[dateKey][userId]) {
            allDayMap[dateKey][userId] = [];
          }

          const bgColor = getEventColor(item);
          const dotColor = getEventBorderColor(item);

          allDayMap[dateKey][userId].push({
            bgColor,
            dotColor,
            item,
            user: scheduler.user,
          });
        });

        // Process time-based events
        timeEvents.forEach((item) => {
          if (!item.startTime || !item.endTime) return;

          const startMoment = moment(item.startTime);
          const endMoment = moment(item.endTime);
          const requestedMoment = moment(requestedDate);

          startMoment.year(requestedMoment.year());
          startMoment.month(requestedMoment.month());
          startMoment.date(requestedMoment.date());

          endMoment.year(requestedMoment.year());
          endMoment.month(requestedMoment.month());
          endMoment.date(requestedMoment.date());

          const bgColor = getEventColor(item);
          const dotColor = getEventBorderColor(item);

          timeMap[dateKey].push({
            bgColor,
            dotColor,
            item: {
              ...item,
              startTime: startMoment.toISOString(),
              endTime: endMoment.toISOString(),
            },
            user: scheduler.user,
          });
        });
      });
    });

    return { allDayEventsMap: allDayMap, timeEventsByDate: timeMap };
  }, [
    groupedAgendaByDate,
    getAllEvents,
    categorizeEvents,
    getEventColor,
    getEventBorderColor,
  ]);

  // Get event display text
  const getEventDisplayText = useCallback(
    (event) => {
      if (!event) return null;
      let eventTitle = event?.title || "";

      // Override title for Leave and Appointment
      if (event?.agendaType === constants.agendaType.LEAVE) {
        eventTitle = "On Leave";
      } else if (event?.agendaType === constants.agendaType.APPOINTMENT) {
        eventTitle = "Patient Appointment";
      }

      const officeName = renderOfficeNameOrEnterprise(event, t);

      return (
        <div className="d-flex flex-column">
          {(!viewSettingFilter.length ||
            viewSettingFilter.includes("title")) && (
            <div className={styles["event-office"]}>{eventTitle}</div>
          )}
          {canShowOfficeName &&
            viewSettingFilter.length > 0 &&
            viewSettingFilter.includes("officeName") && (
              <div className={styles["event-office"]}>{officeName}</div>
            )}
        </div>
      );
    },
    [t, viewSettingFilter, canShowOfficeName],
  );

  // Helper functions for time-based event positioning
  const getTimeInMinutes = useCallback((time) => {
    if (!time) {
      return 0;
    }
    const date = new Date(time);
    if (isNaN(date.getTime())) {
      return 0;
    }
    const hours = date.getHours();
    const minutes = date.getMinutes();
    return hours * 60 + minutes;
  }, []);

  // Helper function to get time range from hours array (view's time range)
  // The time range is always based on the hours displayed in the view (12 AM to 11:59 PM)
  const getTimeRange = useCallback(() => {
    // Use hours array to determine range (00:00 to 23:59)
    const minHour = hours.length > 0 ? hours[0].hour : 0;
    const maxHour = hours.length > 0 ? hours[hours.length - 1].hour : 23;
    return {
      startTime: moment().hour(minHour).minute(0).second(0),
      endTime: moment().hour(maxHour).minute(59).second(59),
    };
  }, [hours]);

  // Helper function to check if time is within allowed range
  // Checks both that the time is within the range AND on the same day
  const isTimeWithinRange = useCallback((timeMoment, timeRange, baseDate) => {
    if (!timeMoment || !timeRange) return true;

    // Check if time is on the same day as baseDate
    if (baseDate && !timeMoment.isSame(baseDate, "day")) {
      return false;
    }

    const timeInMinutes = timeMoment.hour() * 60 + timeMoment.minute();
    const rangeStartMinutes =
      timeRange.startTime.hour() * 60 + timeRange.startTime.minute();
    const rangeEndMinutes =
      timeRange.endTime.hour() * 60 + timeRange.endTime.minute();

    return (
      timeInMinutes >= rangeStartMinutes && timeInMinutes <= rangeEndMinutes
    );
  }, []);

  const calculateOverlapPositions = useCallback(
    (events) => {
      events.sort(
        (a, b) => getTimeInMinutes(a.startTime) - getTimeInMinutes(b.startTime),
      );

      const positions = [];

      for (let i = 0; i < events.length; i++) {
        let overlaps = [events[i]];
        for (let j = i + 1; j < events.length; j++) {
          const endA = getTimeInMinutes(events[i].endTime);
          const startB = getTimeInMinutes(events[j].startTime);

          if (startB < endA) {
            overlaps.push(events[j]);
          }
        }

        const totalOverlap = overlaps.length;
        overlaps.forEach((event, index) => {
          positions.push({
            event,
            widthFactor: 1 / totalOverlap,
            leftOffsetFactor: index / totalOverlap,
          });
        });

        i += overlaps.length - 1;
      }

      return positions;
    },
    [getTimeInMinutes],
  );

  const groupEventsByStartTime = useCallback(
    (events) => {
      const grouped = {};
      events.forEach((event) => {
        const startMinutes = getTimeInMinutes(event.startTime);
        if (!grouped[startMinutes]) {
          grouped[startMinutes] = [];
        }
        grouped[startMinutes].push(event);
      });
      return grouped;
    },
    [getTimeInMinutes],
  );

  const goToDetails = useCallback(
    (id, type, createdByMe, requestedDate, isInvite = false, item) => {
      if (!id || !type) return;
      switch (type) {
        case constants.agendaType.EVENT: {
          let pathname = "/";
          pathname = createdByMe
            ? constants.routes.scheduler.eventDetails.replace(
                ":eventId",
                encodeId(id),
              )
            : isInvite
              ? constants.routes.scheduler.eventRequestDetails.replace(
                  ":eventId",
                  encodeId(id),
                )
              : constants.routes.scheduler.EventWorkingDetails.replace(
                  ":eventId",
                  encodeId(id),
                );
          history.push({
            pathname,
            state: {
              requestedDate,
              fromScreen: "mySchedule",
            },
            search: effectiveOwnerIdString ? `?${effectiveOwnerIdString}` : "",
          });
          break;
        }
        case constants.agendaType.BUSY_SLOT: {
          const pathname = constants.routes.scheduler.busySlotDetail.replace(
            ":busySlotId",
            encodeId(id),
          );
          history.push({
            pathname,
            state: {
              requestedDate,
              fromScreen: "mySchedule",
            },
            search: effectiveOwnerIdString ? `?${effectiveOwnerIdString}` : "",
          });
          break;
        }
        case constants.agendaType.LEAVE: {
          const isBeforeToday = moment(item?.startDate).isBefore(
            moment().startOf("day"),
          );
          if (item?.startDate && !isBeforeToday) {
            history.push({
              pathname: constants.routes.staff.editLeaves.replace(
                ":officeId",
                encodeId(item.officeId),
              ),
              state: { ...item, userId: profile.id, backRedirect: true },
              search: effectiveOwnerIdString ? `?${effectiveOwnerIdString}` : "",
            });
          } else if (isBeforeToday) {
            toast.error("You cannot edit the leaves for past dates");
          }
          break;
        }
        default: {
          return;
        }
      }
    },
    [history,effectiveOwnerIdString,profile?.id],
  );

  // Check if event can be dragged
  const canDragEvent = useCallback(
    (eventItem) => {
      const createdByMe = eventItem.createdById === currentUserId;
      const ownedByMe =
        eventItem.ownerId === selectedOwnerId ||
        eventItem.ownerId === currentUserId;
      const createdByOthers =
        eventItem?.isShift && profile?.id !== eventItem?.createdById;
      const isBusySlot =
        eventItem.agendaType === constants.agendaType.BUSY_SLOT ||
        eventItem.isBusySlot === true;
      const isBlocked = eventItem.agendaType === constants.agendaType.BLOCKED;

      if (isBusySlot || isBlocked) {
        if (isBusySlot) {
          const hasCreator = createdByMe;
          const hasOwner =
            ownedByMe ||
            eventItem.ownerId === selectedOwnerId ||
            eventItem.ownerId === currentUserId;
          const noOwnerInfo = !eventItem.createdById && !eventItem.ownerId;

          // Allow if any ownership check passes, or if no owner info (default allow for user's schedule)
          return hasCreator || hasOwner || noOwnerInfo;
        }
        // For blocked, still check createdById
        return createdByMe;
      }

      // IMPORTANT: Check for shifts BEFORE checking for regular events
      // because shifts have agendaType === EVENT but also have isShift === true
      if (eventItem?.isShift) {
        // If created by me, always allow
        if (createdByMe) {
          return true;
        }
        // If created by others, check if user is owner or admin for that office
        if (createdByOthers && canEditAndViewOthersData(eventItem?.officeId)) {
          return true;
        }
        return false;
      }

      // Regular events (non-shifts) can only be moved by creator
      if (eventItem.agendaType === constants.agendaType.EVENT) {
        return createdByMe;
      }

      return false;
    },
    [currentUserId, selectedOwnerId, profile?.id, canEditAndViewOthersData],
  );

  // Calculate new date and time from drop position with 15-minute snap
  const calculateNewDateTime = useCallback(
    (eventItem, dropX, dropY, cellElement, dayIndex, hourIndex) => {
      if (
        !cellElement ||
        dayIndex < 0 ||
        dayIndex >= weekData.length ||
        !eventItem
      ) {
        return null;
      }

      const cellRect = cellElement.getBoundingClientRect();
      const relativeY = dropY - cellRect.top;
      const cellHeight = cellRect.height;
      const minuteHeight = cellHeight / 60;
      const minutesFromHourStart = relativeY / minuteHeight;

      const snappedMinutes = Math.round(minutesFromHourStart / 15) * 15;
      const clampedMinute = Math.max(0, Math.min(45, snappedMinutes));

      const targetDate = weekData[dayIndex];
      if (!targetDate) return null;

      const newDate = moment(targetDate.date);
      const newStartTime = moment(newDate)
        .hour(hourIndex)
        .minute(clampedMinute)
        .second(0);

      // Get time range for validation (view's time range: 12 AM to 11:59 PM)
      const timeRange = getTimeRange();

      if (!eventItem.startTime || !eventItem.endTime) {
        // Default to 1 hour if no duration available
        const durationMinutes = 60;
        const newEndTime = moment(newStartTime).add(durationMinutes, "minutes");

        // Check if new times are within allowed range (both start and end must be within range and on same day)
        if (
          !isTimeWithinRange(newStartTime, timeRange, newDate) ||
          !isTimeWithinRange(newEndTime, timeRange, newDate)
        ) {
          return { error: "timeRangeExceeded" };
        }
        return {
          newDate: newDate.toDate(),
          newStartTime: newStartTime.toDate(),
          newEndTime: newEndTime.toDate(),
          newStartTimeString: newStartTime.format("YYYY-MM-DDTHH:mm"),
          newEndTimeString: newEndTime.format("YYYY-MM-DDTHH:mm"),
        };
      }

      const originalStart = moment(eventItem.startTime);
      const originalEnd = moment(eventItem.endTime);
      const durationMinutes = originalEnd.diff(originalStart, "minutes");

      const newEndTime = moment(newStartTime).add(durationMinutes, "minutes");

      // Check if new times are within allowed range (both start and end must be within range and on same day)
      if (
        !isTimeWithinRange(newStartTime, timeRange, newDate) ||
        !isTimeWithinRange(newEndTime, timeRange, newDate)
      ) {
        return { error: "timeRangeExceeded" };
      }
      return {
        newDate: newDate.toDate(),
        newStartTime: newStartTime.toDate(),
        newEndTime: newEndTime.toDate(),
        newStartTimeString: newStartTime.format("YYYY-MM-DDTHH:mm"),
        newEndTimeString: newEndTime.format("YYYY-MM-DDTHH:mm"),
      };
    },
    [weekData, getTimeRange, isTimeWithinRange],
  );

  // Helper to check if time changed
  const getTimeCheck = useCallback((alreadyTime, selectedTime) => {
    if (alreadyTime) {
      const selectedStartTime = moment(selectedTime);
      const alreadySelectedStartTime = moment(alreadyTime);
      if (
        selectedStartTime.hour() !== alreadySelectedStartTime.hour() ||
        selectedStartTime.minute() !== alreadySelectedStartTime.minute()
      ) {
        return true;
      }
      return false;
    } else {
      return true;
    }
  }, []);

  // Internal function to perform the actual update
  const performEventUpdate = useCallback(
    async (eventData, newDateTime, editType, originalRequestedDate = null) => {
      if (!newDateTime || !eventData) {
        toast.error(t("scheduler.invalidEventData"));
        return;
      }

      // Check what changed (similar to EditEvent verifyIfUpdated)
      // Use matchedBothDatesValues for consistency with EditEvent
      // This correctly determines if the date changed, accounting for recurring events
      const requestedDateForCheck = originalRequestedDate
        ? originalRequestedDate
        : eventData.requestedDate
          ? moment(eventData.requestedDate).format("YYYY-MM-DD")
          : eventData.startTime
            ? moment(eventData.startTime).format("YYYY-MM-DD")
            : moment(eventData.date || newDateTime.newDate).format(
                "YYYY-MM-DD",
              );

      const dateChangedResult = matchedBothDatesValues({
        eventDetail: eventData,
        repeatedEndDate: eventData.repeatedEndDate
          ? moment(eventData.repeatedEndDate).toDate()
          : moment().add(3, "month").toDate(),
        requestedDate: requestedDateForCheck,
        eventDate: newDateTime.newDate,
      });

      const startTimeChanged = getTimeCheck(
        eventData.startTime,
        newDateTime.newStartTimeString,
      );
      const endTimeChanged = getTimeCheck(
        eventData.endTime,
        newDateTime.newEndTimeString,
      );

      // Get the original requested date (the date of the instance being edited)
      // This should be the original date, not the new date (same as EditEvent)
      const originalRequestedDateForParams = originalRequestedDate
        ? moment(originalRequestedDate).format("YYYY-MM-DDTHH:mm")
        : eventData.requestedDate
          ? moment(eventData.requestedDate).format("YYYY-MM-DDTHH:mm")
          : eventData.startTime
            ? moment(eventData.startTime).format("YYYY-MM-DDTHH:mm")
            : moment(newDateTime.newDate).format("YYYY-MM-DDTHH:mm");

      // Get requestedDate for Date calculation (same as EditEvent line 193)
      // This is the date of the instance being edited
      const requestedDateForDateCalc = originalRequestedDate
        ? moment(originalRequestedDate).toDate()
        : eventData.requestedDate
          ? moment(eventData.requestedDate).toDate()
          : eventData.startTime
            ? moment(eventData.startTime).toDate()
            : null;

      // Get eventDate (the new date where we're dropping)
      const eventDate = moment(newDateTime.newDate).toDate();

      // Calculate Date parameter using EXACT same logic as EditEvent (lines 794-801)
      const params = {
        SchedulerEventId: eventData.id,
        OwnerId: selectedOwnerId || currentUserId,
        Title: eventData.title,
        Date: moment(
          requestedDateForDateCalc &&
            moment(requestedDateForDateCalc)
              .startOf("day")
              .isSame(moment(eventDate).startOf("day"))
            ? eventData?.date
            : eventDate,
        ).format("YYYY-MM-DDTHH:mm"),
        StartTime: newDateTime.newStartTimeString,
        EndTime: newDateTime.newEndTimeString,
        IsAllDayEvent: eventData.isAllDayEvent || false,
        RepeatedType: eventData.repeatedType || 1,
        RepeatedDay: moment(newDateTime.newDate).isoWeekday(),
        RepeatedEndDate:
          eventData.repeatedType > 1 && eventData.repeatedEndDate
            ? moment(eventData.repeatedEndDate).format("YYYY-MM-DD")
            : null,
        Location: eventData.location || "",
        BlockForPatient: eventData.blockForPatient || false,
        Note: eventData.note || "",
        PublishAsEvent: eventData.publishAsEvent || false,
        eventEmployees: Array.isArray(eventData.eventEmployees)
          ? eventData.eventEmployees
          : [],
        EventRoles: Array.isArray(eventData.eventRoles)
          ? eventData.eventRoles
          : [],
        EventTags: Array.isArray(eventData.eventTags)
          ? eventData.eventTags
          : [],
        CreatedAt: moment().format("YYYY-MM-DDTHH:mm"),
        ReminderBeforeInMins: eventData.reminderBeforeInMins || 0,
        isDraft: eventData.isDraft || false,
        requestedDate: originalRequestedDateForParams,
        timezoneCode: eventData.timezoneCode || moment.tz.guess(),
        EditType: editType || editTypesVariables.allEvent,
        UpdateFlags: {
          isTitleEdit: false,
          isStartTimeEdit: startTimeChanged,
          isEndTimeEdit: endTimeChanged,
          isAllDayEventEdit: false,
          isNoteEdit: false,
          isLocationEdit: false,
          isReminderBeforeInMinsEdit: false,
          isEnterpriseEdit: false,
          isOfficeIdEdit: false,
          EmployeeIds: [],
          RoleIds: [],
          IsRepeatTypeChange: false,
          IsEventDateChange: dateChangedResult.startDateChanged,
          IsRepeatedEndDateChange: dateChangedResult.endDateChanged,
          IsBlockForPatientChange: false,
          tagsIds: false,
        },
      };

      if (eventData.isShift) {
        params.Title = eventData.title || "";
        delete params.Location;
        delete params.Note;
        if (
          Array.isArray(eventData.eventEmployees) &&
          eventData.eventEmployees.length > 0 &&
          eventData.eventEmployees[0]
        ) {
          params.officeId = eventData.eventEmployees[0].officeId || null;
        }
      } else {
        params.IsAutoApprove = eventData.isAutoApprove || false;
      }

      try {
        setterCustomSchduler({
          params,
          repeatedType: eventData.repeatedType || 1,
          repeatedTypeFrequency: eventData.customRepeatPattern?.unit || 1,
          repeatedTypeEvery: eventData.customRepeatPattern?.interval || 1,
          selectionType: true,
          schedule:
            eventData.customRepeatPattern?.daysOfWeek?.map(
              (d) => d?.dayOfWeek,
            ) || [],
          selectedSlotDate:
            eventData.customRepeatPattern?.daysOfMonth?.map((d) => d?.day) ||
            [],
          selectedWeek: eventData.customRepeatPattern?.monthWeekNumber || 1,
          selectedDay:
            eventData.customRepeatPattern?.daysOfWeek?.map(
              (d) => d?.dayOfWeek,
            ) || [],
          selectedMonths:
            eventData.customRepeatPattern?.monthsOfYear?.map((m) => m?.month) ||
            [],
          isDaySwitchOn: false,
          edit: true,
          eventDetail: eventData,
        });
      } catch (error) {
        console.warn("Error in setterCustomSchduler:", error);
      }

      try {
        const preferredOption = {
          dropdown: eventData.publishAsEvent
            ? [{ id: "allowInvitees" }]
            : eventData.isDraft
              ? [{ id: "saveDraft" }]
              : [{ id: "inviteesOnly" }],
          otherOptions: [],
        };
        setterPublishOffices({
          preferredOption,
          userIds: Array.isArray(eventData.eventEmployees)
            ? eventData.eventEmployees
            : [],
          publishAsEvent: eventData.publishAsEvent
            ? "allowInvitees"
            : eventData.isDraft
              ? "saveDraft"
              : "inviteesOnly",
          nonInviteesPublish: eventData.publishedType || "allOffices",
          params,
          officeIdForNonInvitee: Array.isArray(
            eventData.schedulerEventPublishedOffices,
          )
            ? eventData.schedulerEventPublishedOffices.filter(
                (office) => office && office.id,
              )
            : [],
          edit: true,
          isDraft: eventData.isDraft || false,
        });
      } catch (error) {
        console.warn("Error in setterPublishOffices:", error);
      }

      await createSchedulerEventMutation.mutateAsync(params);

      queryClient.invalidateQueries(["agenda"]);

      toast.success(t("staff.eventUpdated"));
    },
    [
      selectedOwnerId,
      currentUserId,
      createSchedulerEventMutation,
      t,
      queryClient,
      getTimeCheck,
    ],
  );

  // Update event/shift via API (with EditEvent-style checks and modal)
  const updateEventAfterDrag = useCallback(
    async (eventItem, newDateTime, editType = null) => {
      if (!newDateTime || !eventItem || !eventItem.id) {
        toast.error(t("scheduler.invalidEventData"));
        return;
      }

      try {
        // Fetch current event details to get all required fields
        // Use the original requestedDate from the event if available, otherwise use new date
        const originalRequestedDate = draggedEventDateRef.current
          ? draggedEventDateRef.current
          : moment(
              draggedEvent.requestedDate ||
                eventItem.startTime ||
                newDateTime.newDate,
            ).format("YYYY-MM-DD");
        const eventDetails = await getEventListDetails(
          eventItem.id,
          originalRequestedDate,
        );
        const eventData = eventDetails?.data;

        if (!eventData) {
          toast.error(t("scheduler.eventNotFound"));
          return;
        }

        // Check what changed (similar to EditEvent verifyIfUpdated)
        // Calculate dateChanged using matchedBothDatesValues (same as EditEvent)
        const dateChangedResult = matchedBothDatesValues({
          eventDetail: eventData,
          repeatedEndDate: eventData.repeatedEndDate
            ? moment(eventData.repeatedEndDate).toDate()
            : moment().add(3, "month").toDate(),
          requestedDate: originalRequestedDate,
          eventDate: newDateTime.newDate,
        });

        // Calculate repeatedOptionChanged (should be false for drag-drop as we're not changing pattern)
        const repeatedOptionChanged = matchedBothPatternValues({
          repeatedType: eventData.repeatedType || 1,
          repeatedTypeFrequency: eventData.customRepeatPattern?.unit || 1,
          repeatedTypeEvery: eventData.customRepeatPattern?.interval || 1,
          selectionType: true,
          schedule:
            eventData.customRepeatPattern?.daysOfWeek?.map(
              (d) => d?.dayOfWeek,
            ) || [],
          selectedSlotDate:
            eventData.customRepeatPattern?.daysOfMonth?.map((d) => d?.day) ||
            [],
          selectedWeek: eventData.customRepeatPattern?.monthWeekNumber || 1,
          selectedDay:
            eventData.customRepeatPattern?.daysOfWeek?.map(
              (d) => d?.dayOfWeek,
            ) || [],
          selectedMonths:
            eventData.customRepeatPattern?.monthsOfYear?.map((m) => m?.month) ||
            [],
          isDaySwitchOn: false,
          edit: true,
          eventDetail: eventData,
        });

        // Calculate anyChangedForDate (same as EditEvent)
        const anyChangedForDate = repeatedOptionChanged;

        const startTimeChanged = getTimeCheck(
          eventData.startTime,
          newDateTime.newStartTimeString,
        );
        const endTimeChanged = getTimeCheck(
          eventData.endTime,
          newDateTime.newEndTimeString,
        );

        const hasChanges =
          dateChangedResult.startDateChanged ||
          dateChangedResult.endDateChanged ||
          startTimeChanged ||
          endTimeChanged;

        // Validate repeatedEndDate: if new date is after repeatedEndDate, show error
        const neverCheck =
          eventData.repeatedType ===
          addEditEventsConst.repeatedOptionsValues.never;
        if (!neverCheck && eventData.repeatedEndDate) {
          const newDateStr = moment(newDateTime.newDate).format("YYYY-MM-DD");
          const repeatedEndDateStr = moment(eventData.repeatedEndDate).format(
            "YYYY-MM-DD",
          );
          // Check if new date is NOT before repeatedEndDate and they're not equal
          if (
            !moment(newDateStr).isBefore(repeatedEndDateStr) &&
            newDateStr !== repeatedEndDateStr
          ) {
            toast.error(t("form.errors.endGreater"));
            return;
          }
        }

        // Check if event is repeating (similar to EditEvent logic)
        const isNotDrafted = !eventData.isDraft;

        // If event is repeating, has changes, is not a draft, and no editType provided, show modal
        if (!neverCheck && !editType && hasChanges && isNotDrafted) {
          // Store update parameters for modal confirmation
          setPendingDragUpdate({
            eventData,
            newDateTime,
            anyChangedForDate,
            originalRequestedDate,
          });
          setEditModalConfirmation(true);
          return;
        }

        // Determine EditType based on repeatedType (similar to EditEvent)
        let finalEditType = editType;
        if (!finalEditType) {
          if (neverCheck) {
            // For non-repeating events, use allEvent (3)
            finalEditType = editTypesVariables.allEvent;
          } else {
            // For repeating events without modal selection, default to allEvent
            finalEditType = editTypesVariables.allEvent;
          }
        }

        // Perform the update - pass originalRequestedDate for correct date change detection
        await performEventUpdate(
          eventData,
          newDateTime,
          finalEditType,
          originalRequestedDate,
        );
      } catch (error) {
        toast.error(error.message || t("scheduler.updateFailed"));
        throw error;
      }
    },
    [t, getTimeCheck, performEventUpdate, draggedEvent],
  );

  // Internal function to perform the actual busy slot update (called directly or from modal)
  const performBusySlotUpdate = useCallback(
    async (
      busySlotData,
      newDateTime,
      editType,
      officeDetails,
      officeIds,
      busySlotEmployeeView,
      isAllDayBusy,
      repeatedType,
      repeatedEndDate,
      originalRequestedDate,
      UpdateFlags,
    ) => {
      if (!newDateTime || !busySlotData) {
        toast.error(t("scheduler.invalidEventData"));
        return;
      }

      // Format dates and times
      const eventDateMoment = moment(newDateTime.newDate);
      const startTime = moment(newDateTime.newStartTime);
      const endTime = moment(newDateTime.newEndTime);

      // Get requestedDate for Date calculation (same as EditEvent line 193)
      // This is the date of the instance being edited
      const requestedDateForDateCalc = originalRequestedDate
        ? moment(originalRequestedDate).toDate()
        : busySlotData.requestedDate
          ? moment(busySlotData.requestedDate).toDate()
          : busySlotData.startTime
            ? moment(busySlotData.startTime).toDate()
            : null;

      // Get eventDate (the new date where we're dropping)
      const eventDate = eventDateMoment.toDate();

      // Build params exactly like AddBusySlots
      // Calculate Date parameter using EXACT same logic as EditEvent (lines 794-801)
      const params = {
        OwnerId: officeDetails.ownerId,
        OfficeIds: officeIds,
        Title: busySlotData.title || "",
        Date: moment(
          requestedDateForDateCalc &&
            moment(requestedDateForDateCalc)
              .startOf("day")
              .isSame(moment(eventDate).startOf("day"))
            ? busySlotData?.date
            : eventDate,
        ).format("YYYY-MM-DDTHH:mm"),
        StartTime: formatBusySlotTimeValue({
          eventDateMoment,
          isAllDayBusy,
          timeValue: startTime,
          startOrEnd: "startOf",
        }),
        EndTime: formatBusySlotTimeValue({
          eventDateMoment,
          isAllDayBusy,
          timeValue: endTime,
          startOrEnd: "endOf",
        }),
        repeatedType: +repeatedType,
        CreatedAt: moment().format("YYYY-MM-DDTHH:mm"),
        repeatedDay: +repeatedType === 1 ? null : eventDateMoment.isoWeekday(),
        RepeatedEndDate:
          +repeatedType === 1
            ? null
            : moment(repeatedEndDate).format("YYYY-MM-DD"),
        Location: busySlotData.location || "",
        Description: busySlotData.description || "",
        IsNotifyEmployee: busySlotData.isNotifyEmployee || false,
        BusySlotEmployeeView: busySlotEmployeeView,
        IsAllDayBusy: isAllDayBusy,
      };

      // Call setterCustomSchduler to handle custom repeat patterns
      try {
        setterCustomSchduler({
          params,
          repeatedType: repeatedType,
          repeatedTypeFrequency: busySlotData.customRepeatPattern?.unit || 1,
          repeatedTypeEvery: busySlotData.customRepeatPattern?.interval || 1,
          selectionType: true,
          schedule:
            busySlotData.customRepeatPattern?.daysOfWeek?.map(
              (d) => d?.dayOfWeek,
            ) || [],
          selectedSlotDate:
            busySlotData.customRepeatPattern?.daysOfMonth?.map((d) => d?.day) ||
            [],
          selectedWeek: busySlotData.customRepeatPattern?.monthWeekNumber || 1,
          selectedDay:
            busySlotData.customRepeatPattern?.daysOfWeek?.map(
              (d) => d?.dayOfWeek,
            ) || [],
          selectedMonths:
            busySlotData.customRepeatPattern?.monthsOfYear?.map(
              (m) => m?.month,
            ) || [],
          isDaySwitchOn: false,
          edit: true,
          eventDetail: busySlotData,
        });
      } catch (error) {
        console.warn("Error in setterCustomSchduler:", error);
      }

      // Add edit-specific fields
      params.UpdateFlags = UpdateFlags;
      params.EditType = editType || 3;
      params.SchedulerBusySlotId = busySlotData.id;
      params.RequestedDate = originalRequestedDate;
      // Delete isRepeated when editing (as per AddBusySlots)
      delete params.isRepeated;

      await updateBusySlot(params);

      // Invalidate query cache to refresh data
      queryClient.invalidateQueries(["agenda"]);

      toast.success(t("scheduler.busySlotUpdated"));
    },
    [t, queryClient],
  );

  // Update busy slot via API (matching EditBusySlot handleBlock logic)
  const updateBusySlotAfterDrag = useCallback(
    async (eventItem, newDateTime, editType = null) => {
      if (!newDateTime || !eventItem || !eventItem.id) {
        toast.error(t("scheduler.invalidEventData"));
        return;
      }

      try {
        // Fetch current busy slot details to get all required fields (like updateEventAfterDrag)
        // Use the original requestedDate from the event if available, otherwise use new date
        const originalRequestedDate = draggedEventDateRef.current
          ? draggedEventDateRef.current
          : moment(
              draggedEvent.requestedDate ||
                eventItem.startTime ||
                eventItem.date ||
                newDateTime.newDate,
            ).format("YYYY-MM-DD");

        let busySlotDetails = null;
        try {
          busySlotDetails = await getBusySlot({
            queryKey: [
              "busy-slot-details",
              eventItem.id,
              originalRequestedDate,
            ],
          });
        } catch (error) {
          toast.error(error.message || t("scheduler.updateFailed"));
          return;
        }

        const busySlotData = busySlotDetails?.data;

        if (!busySlotData) {
          toast.error(t("scheduler.eventNotFound"));
          return;
        }

        // Fetch office details to get correct ownerId and officeId (like EditBusySlot)
        const officeId = busySlotData.officeId || eventItem.officeId;
        if (!officeId) {
          toast.error(t("scheduler.officeIdRequired"));
          return;
        }

        let officeDetails = null;
        try {
          officeDetails = await getOfficeDetail(officeId);
        } catch (error) {
          toast.error(
            error.message || t("scheduler.failedToFetchOfficeDetails"),
          );
          return;
        }

        if (!officeDetails) {
          toast.error(t("scheduler.officeDetailsNotFound"));
          return;
        }

        // Determine if it's all-day event
        const isAllDayBusy =
          eventItem.isAllDayBusy ||
          eventItem.isAllDayEvent ||
          !eventItem.startTime ||
          !eventItem.endTime;

        // Format dates and times exactly like AddBusySlots
        const eventDate = moment(newDateTime.newDate);
        const startTime = moment(newDateTime.newStartTime);
        const endTime = moment(newDateTime.newEndTime);

        const repeatedType = +(busySlotData.repeatedType || 1);
        const repeatedEndDate = busySlotData.repeatedEndDate
          ? moment(busySlotData.repeatedEndDate)
          : moment().toDate();

        // Build OfficeIds array - if schedulerBusySlotOffice exists, use it, otherwise create new
        let officeIds = [];
        if (
          busySlotData.schedulerBusySlotOffice &&
          Array.isArray(busySlotData.schedulerBusySlotOffice)
        ) {
          officeIds = busySlotData.schedulerBusySlotOffice.map((ele) => ({
            id: ele.externalId || ele.id || 0,
            SchedulerBusySlotId: busySlotData.id,
            OfficeId: ele.officeId,
            IsDeleted: false,
          }));
        } else {
          // If no schedulerBusySlotOffice, create from officeId
          officeIds = [
            {
              id: 0,
              SchedulerBusySlotId: busySlotData.id,
              OfficeId: officeId,
              IsDeleted: false,
            },
          ];
        }

        // Build BusySlotEmployeeView
        const busySlotEmployeeView = (
          busySlotData.busySlotNotifiedUsers ||
          eventItem.busySlotNotifiedUsers ||
          []
        ).map((item) => ({
          userid: item.userId,
          OfficeId: item.officeId || officeId,
        }));

        // Calculate dateChanged using matchedBothDatesValues (same as AddBusySlots)
        const dateChangedResult = matchedBothDatesValues({
          eventDetail: busySlotData,
          repeatedEndDate: repeatedEndDate,
          requestedDate: originalRequestedDate,
          eventDate: eventDate.toDate(),
        });

        // Calculate repeatedOptionChanged using matchedBothPatternValues (same as AddBusySlots)
        const repeatedOptionChanged = matchedBothPatternValues({
          repeatedType: repeatedType,
          repeatedTypeFrequency: busySlotData.customRepeatPattern?.unit || 1,
          repeatedTypeEvery: busySlotData.customRepeatPattern?.interval || 1,
          selectionType: true,
          schedule:
            busySlotData.customRepeatPattern?.daysOfWeek?.map(
              (d) => d?.dayOfWeek,
            ) || [],
          selectedSlotDate:
            busySlotData.customRepeatPattern?.daysOfMonth?.map((d) => d?.day) ||
            [],
          selectedWeek: busySlotData.customRepeatPattern?.monthWeekNumber || 1,
          selectedDay:
            busySlotData.customRepeatPattern?.daysOfWeek?.map(
              (d) => d?.dayOfWeek,
            ) || [],
          selectedMonths:
            busySlotData.customRepeatPattern?.monthsOfYear?.map(
              (m) => m?.month,
            ) || [],
          isDaySwitchOn: false,
          edit: true,
          eventDetail: busySlotData,
        });

        // Calculate anyChangedForDate (same as AddBusySlots)
        const anyChangedForDate =
          dateChangedResult.startDateChanged ||
          dateChangedResult.endDateChanged ||
          repeatedOptionChanged;

        // Validate repeatedEndDate: if new date is after repeatedEndDate, show error
        const neverCheck =
          repeatedType === addEditEventsConst.repeatedOptionsValues.never;
        if (!neverCheck && busySlotData.repeatedEndDate) {
          const newDateStr = moment(eventDate).format("YYYY-MM-DD");
          const repeatedEndDateStr = moment(
            busySlotData.repeatedEndDate,
          ).format("YYYY-MM-DD");
          // Check if new date is NOT before repeatedEndDate and they're not equal
          if (
            !moment(newDateStr).isBefore(repeatedEndDateStr) &&
            newDateStr !== repeatedEndDateStr
          ) {
            toast.error(t("form.errors.endGreater"));
            return;
          }
        }

        // Build UpdateFlags (same as AddBusySlots verifyIfUpdated)
        const UpdateFlags = {
          isTitleEdit: false,
          isStartTimeEdit: getTimeCheck(
            busySlotData.startTime,
            formatBusySlotTimeValue({
              eventDateMoment: eventDate,
              isAllDayBusy,
              timeValue: startTime,
              startOrEnd: "startOf",
            }),
          ),
          isEndTimeEdit: getTimeCheck(
            busySlotData.endTime,
            formatBusySlotTimeValue({
              eventDateMoment: eventDate,
              isAllDayBusy,
              timeValue: endTime,
              startOrEnd: "endOf",
            }),
          ),
          IsAllDayBusyEdit: busySlotData.isAllDayBusy !== isAllDayBusy,
          isLocationEdit: false,
          IsDescriptionEdit: false,
          IsOfficeEdit: false,
          IsNotifiedUserEdit: false,
          IsNotifiedStaffListEdit: false,
          IsRepeatTypeChange: repeatedOptionChanged,
          IsBusySlotDateChange: dateChangedResult.startDateChanged,
          IsRepeatedEndDateChange: dateChangedResult.endDateChanged,
          OfficeIds: [],
          BusySlotEmployeeView: [],
        };

        // Check if there are any changes (similar to AddBusySlots verifyIfUpdated)
        const verified = {
          UpdateFlags,
          changedCheck: Object.values(UpdateFlags || {})?.some((value) =>
            typeof value === "boolean" ? value : value?.length > 0,
          ),
        };

        // Check if we need to show the modal (same logic as AddBusySlots handleBlock)
        // If busy slot is repeating, has changes, and no editType provided, show modal
        if (
          !neverCheck &&
          !editType &&
          (verified.changedCheck || anyChangedForDate)
        ) {
          // Store update parameters for modal confirmation
          setPendingDragUpdate({
            busySlotData,
            newDateTime,
            anyChangedForDate,
            isBusySlot: true,
            UpdateFlags,
            officeDetails,
            officeIds,
            busySlotEmployeeView,
            isAllDayBusy,
            repeatedType,
            repeatedEndDate,
            originalRequestedDate,
            eventItem,
          });
          setEditModalConfirmation(true);
          return;
        }

        // Determine EditType based on repeatedType (similar to AddBusySlots)
        let finalEditType = editType;
        if (!finalEditType) {
          if (neverCheck) {
            // For non-repeating busy slots, use allEvent (3)
            finalEditType = 3;
          } else {
            // For repeating busy slots without modal selection, default to allEvent
            finalEditType = 3;
          }
        }

        // Perform the update
        await performBusySlotUpdate(
          busySlotData,
          newDateTime,
          finalEditType,
          officeDetails,
          officeIds,
          busySlotEmployeeView,
          isAllDayBusy,
          repeatedType,
          repeatedEndDate,
          originalRequestedDate,
          UpdateFlags,
        );
      } catch (error) {
        toast.error(error.message || t("scheduler.updateFailed"));
        throw error;
      }
    },
    [t, queryClient, getTimeCheck, performBusySlotUpdate, draggedEvent],
  );

  // Handle drag start
  const handleDragStart = useCallback(
    (e, eventItem, fullEvent, originalDateKey = null) => {
      if (!canDragEvent(eventItem)) {
        e.preventDefault();
        return;
      }

      const rect = e.currentTarget.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;

      let originalDate = null;

      if (originalDateKey) {
        const dateObj = groupedAgendaByDate?.find(
          (d) =>
            moment(d.requestedDate).format("MMM DD, ddd") === originalDateKey,
        );
        if (dateObj) {
          originalDate = moment(dateObj.requestedDate).startOf("day");
        } else {
          const parsedDate = moment(originalDateKey, "MMM DD, ddd");
          if (parsedDate.isValid()) {
            const currentYear = moment().year();
            originalDate = parsedDate.year(currentYear).startOf("day");
          }
        }
      }

      if (!originalDate) {
        if (eventItem.date) {
          originalDate = moment(eventItem.date).startOf("day");
        } else if (eventItem.startTime) {
          originalDate = moment(eventItem.startTime).startOf("day");
        }
      }

      draggedEventDateRef.current = originalDateKey.format("YYYY-MM-DD");

      setDraggedEvent({
        eventItem,
        fullEvent,
        originalDate: originalDate ? originalDate.format("YYYY-MM-DD") : null,
        requestedDate: originalDateKey
          ? moment(originalDateKey).format("YYYY-MM-DD")
          : null,
      });
      setIsDragging(true);

      const dragImage = e.currentTarget.cloneNode(true);
      dragImage.style.opacity = "0";
      dragImage.style.position = "absolute";
      dragImage.style.top = "-1000px";
      document.body.appendChild(dragImage);
      e.dataTransfer.setDragImage(dragImage, offsetX, offsetY);
      setTimeout(() => document.body.removeChild(dragImage), 0);
    },
    [canDragEvent, groupedAgendaByDate],
  );

  // Check if event is all-day
  const isAllDayEvent = useCallback((eventItem) => {
    return (
      eventItem.isAllDayEvent ||
      eventItem.isAllDayBusy ||
      !eventItem.startTime ||
      !eventItem.endTime
    );
  }, []);

  // Handle drag over
  const handleDragOver = useCallback(
    (e, dayIndex, hourIndex, isAllDayRow = false) => {
      if (!draggedEvent) {
        e.preventDefault();
        return;
      }

      const { eventItem } = draggedEvent;
      const isDraggedAllDay = isAllDayEvent(eventItem);

      if (isDraggedAllDay && !isAllDayRow) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "none";
        setDragPreviewPosition(null);
        return;
      }
      if (!isDraggedAllDay && isAllDayRow) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "none";
        setDragPreviewPosition(null);
        return;
      }

      e.preventDefault();
      e.dataTransfer.dropEffect = "move";

      if (!isAllDayRow && !isDraggedAllDay && hourIndex !== undefined) {
        const cellElement = e.currentTarget;
        const cellRect = cellElement.getBoundingClientRect();
        const relativeY = e.clientY - cellRect.top;
        const cellHeight = cellRect.height;
        const minuteHeight = cellHeight / 60;
        const minutesFromHourStart = relativeY / minuteHeight;

        const snappedMinutes = Math.round(minutesFromHourStart / 15) * 15;
        const clampedMinute = Math.max(0, Math.min(45, snappedMinutes));

        const originalStart = moment(eventItem.startTime);
        const originalEnd = moment(eventItem.endTime);
        const durationMinutes = originalEnd.diff(originalStart, "minutes");

        const topPercent = (clampedMinute / 60) * 100;
        const heightPercent = (durationMinutes / 60) * 100;

        setDragPreviewPosition({
          dayIndex,
          hourIndex,
          topPercent,
          heightPercent,
          clampedMinute,
        });
      } else if (isAllDayRow && isDraggedAllDay) {
        setDragPreviewPosition({
          dayIndex,
          isAllDay: true,
        });
      }
    },
    [draggedEvent, isAllDayEvent],
  );

  // Calculate new date for all-day events
  const calculateNewDateForAllDay = useCallback(
    (eventItem, dayIndex, originalDateString = null) => {
      if (dayIndex < 0 || dayIndex >= weekData.length) {
        return null;
      }

      const targetDate = weekData[dayIndex];
      if (!targetDate) return null;

      const newDate = moment(targetDate.date).startOf("day");

      let originalDate = null;
      if (originalDateString) {
        originalDate = moment(originalDateString).startOf("day");
      } else if (eventItem.date) {
        originalDate = moment(eventItem.date).startOf("day");
      } else if (eventItem.startTime) {
        originalDate = moment(eventItem.startTime).startOf("day");
      }

      if (originalDate && newDate.isSame(originalDate, "day")) {
        return null;
      }

      return {
        newDate: newDate.toDate(),
        newStartTime: newDate.startOf("day").toDate(),
        newEndTime: newDate.endOf("day").toDate(),
        newStartTimeString: newDate.startOf("day").format("YYYY-MM-DDTHH:mm"),
        newEndTimeString: newDate.endOf("day").format("YYYY-MM-DDTHH:mm"),
      };
    },
    [weekData],
  );

  // Handle drop
  const handleDrop = useCallback(
    async (e, dayIndex, hourIndex, isAllDayRow = false) => {
      e.preventDefault();
      if (!draggedEvent || !draggedEvent.eventItem) {
        setIsDragging(false);
        setDraggedEvent(null);
        setDragPreviewPosition(null);
        return;
      }

      const { eventItem } = draggedEvent;
      if (!eventItem) {
        setIsDragging(false);
        setDraggedEvent(null);
        setDragPreviewPosition(null);
        return;
      }

      const isDraggedAllDay = isAllDayEvent(eventItem);

      if (isDraggedAllDay && !isAllDayRow) {
        toast.error(t("scheduler.cannotMoveAllDayToTimeBased"));
        setIsDragging(false);
        setDraggedEvent(null);
        setDragPreviewPosition(null);
        return;
      }
      if (!isDraggedAllDay && isAllDayRow) {
        toast.error(t("scheduler.cannotMoveTimeBasedToAllDay"));
        setIsDragging(false);
        setDraggedEvent(null);
        setDragPreviewPosition(null);
        return;
      }

      if (isAllDayRow && isDraggedAllDay) {
        if (!eventItem || !eventItem.id) {
          toast.error(t("scheduler.invalidEventData"));
          setIsDragging(false);
          setDraggedEvent(null);
          setDragPreviewPosition(null);
          return;
        }

        const newDate = calculateNewDateForAllDay(
          eventItem,
          dayIndex,
          draggedEvent.originalDate,
        );
        if (!newDate) {
          setIsDragging(false);
          setDraggedEvent(null);
          return;
        }

        setIsDragDropLoading(true);
        try {
          if (
            eventItem.agendaType === constants.agendaType.BUSY_SLOT ||
            eventItem.agendaType === constants.agendaType.BLOCKED
          ) {
            await updateBusySlotAfterDrag(eventItem, newDate);
          } else {
            await updateEventAfterDrag(eventItem, newDate);
          }
        } catch (error) {
          // Error already shown in update functions
        } finally {
          setIsDragDropLoading(false);
          setIsDragging(false);
          setDraggedEvent(null);
          setDragPreviewPosition(null);
        }
        return;
      }

      if (!eventItem || !eventItem.id) {
        toast.error("Invalid event data");
        setIsDragging(false);
        setDraggedEvent(null);
        setDragPreviewPosition(null);
        return;
      }

      const cellElement = e.currentTarget;
      const newDateTime = calculateNewDateTime(
        eventItem,
        e.clientX,
        e.clientY,
        cellElement,
        dayIndex,
        hourIndex,
      );

      // Check for time range exceeded error
      if (newDateTime && newDateTime.error === "timeRangeExceeded") {
        toast.error("You can't move the event outside the time range");
        setIsDragging(false);
        setDraggedEvent(null);
        setDragPreviewPosition(null);
        return;
      }

      if (!newDateTime) {
        setIsDragging(false);
        setDraggedEvent(null);
        setDragPreviewPosition(null);
        return;
      }

      // Check if dropped on same time AND same date - if so, don't call API
      if (eventItem.startTime && newDateTime.newStartTime) {
        const originalStartTime = moment(eventItem.startTime);
        const newStartTime = moment(newDateTime.newStartTime);

        // Compare both date and time
        const isSameDate = originalStartTime.isSame(newStartTime, "day");
        const isSameTime =
          originalStartTime.hour() === newStartTime.hour() &&
          originalStartTime.minute() === newStartTime.minute();

        if (isSameDate && isSameTime) {
          setIsDragging(false);
          setDraggedEvent(null);
          setDragPreviewPosition(null);
          return;
        }
      }

      setIsDragDropLoading(true);
      try {
        if (
          eventItem.agendaType === constants.agendaType.BUSY_SLOT ||
          eventItem.agendaType === constants.agendaType.BLOCKED
        ) {
          await updateBusySlotAfterDrag(eventItem, newDateTime);
        } else {
          await updateEventAfterDrag(eventItem, newDateTime);
        }
      } catch (error) {
        // Error already shown in update functions
      } finally {
        setIsDragDropLoading(false);
        setIsDragging(false);
        setDraggedEvent(null);
        setDragPreviewPosition(null);
      }
    },
    [
      draggedEvent,
      isAllDayEvent,
      calculateNewDateTime,
      calculateNewDateForAllDay,
      updateEventAfterDrag,
      updateBusySlotAfterDrag,
      currentDate,
      onDateChange,
      t,
      weekData,
    ],
  );

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    if (!isDragDropLoading) {
      setIsDragging(false);
      setDraggedEvent(null);
      setDragPreviewPosition(null);
    }
  }, [isDragDropLoading]);

  const renderEventContent = (event, dotColor) => {
    if (!event) return null;
    const eventItem = event.item || event;
    if (!eventItem) return null;
    return (
      <span className={styles.eventTextWrapper}>
        <span
          className={styles.eventDot}
          style={{ backgroundColor: dotColor || "#005f99" }}
        ></span>
        <span className={styles.eventTextContent}>
          {getEventDisplayText(eventItem)}
        </span>
      </span>
    );
  };

  // Scroll to highlighted date and cleanup query param (matching AgendaWeeklyView)
  useEffect(() => {
    if (!search || !groupedAgendaByDate?.length || !agenda?.length) return;
    if (highlightRef.current) {
      setTimeout(() => {
        highlightRef.current.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "center",
        });
      }, 500);
    }
    const timeOut = setTimeout(() => {
      const queryParams = new URLSearchParams(search);
      if (queryParams.has(showTime)) {
        queryParams.delete(showTime);
        history.replace({
          search: queryParams.toString(),
        });
      }
    }, 3000);
    return () => {
      clearTimeout(timeOut);
    };
  }, [search, groupedAgendaByDate, agenda, history]);

  if (isLoading || isFreeStotLoading) {
    return <Loader />;
  }

  // Don't show empty if we're still waiting for data or if agenda hasn't been set yet
  if (
    !agendaFilter?.length &&
    !isLoading &&
    agenda !== undefined &&
    agenda !== null
  ) {
    return <Empty Message={t("scheduler.noEventsFound")} />;
  }

  // Check if any day has a holiday
  const hasAnyHoliday = days.some((day) => {
    const dateObj = groupedAgendaByDate?.find(
      (d) => moment(d.requestedDate).format("MMM DD, ddd") === day.date,
    );
    return (dateObj?.holidayList || []).length > 0;
  });

  return (
    <>
      {isDragDropLoading && <Loader />}
      <div
        ref={containerRef}
        className={styles.calendarWrapper}
        style={{ height: dynamicHeight, overflow: "auto" }}
      >
        <table className={styles.calendarTable}>
          <thead>
            {/* Row 1: Date headers */}
            <tr>
              <th className={styles.timeHeader}>
                <div className={styles.empty}></div>
              </th>
              {days.map((day, i) => {
                const dayMoment =
                  day.momentDate || moment(day.date, "MMM DD, ddd");
                const dayDateString = dayMoment
                  ? dayMoment.format("YYYY-MM-DDTHH:mm:ss")
                  : day.date;
                const highlightCheck =
                  dateMatchForHighlight &&
                  dayMoment &&
                  moment(dateMatchForHighlight)
                    .startOf("day")
                    .isSame(dayMoment.startOf("day"));
                return (
                  <th
                    key={i}
                    ref={highlightCheck ? highlightRef : undefined}
                    className={`${styles.dayHeader} ${
                      schedulerStyles.custom_th
                    } ${schedulerStyles.custom_th_dots} ${
                      highlightCheck ? "blinking-background" : ""
                    }`}
                  >
                    <div className={schedulerStyles.weekly_agenda_header}>
                      {day.date}
                      {dayMoment && !isPastDate(dayDateString) && (
                        <UncontrolledDropdown
                          className={schedulerStyles.ellipsis_position_dotss}
                        >
                          <DropdownToggle
                            caret={false}
                            tag="div"
                            className="cursor-pointer"
                          >
                            <div
                              className={
                                schedulerStyles.ellipsis_icon +
                                " " +
                                "ellipsis-icon"
                              }
                            >
                              <ThreeDots />
                            </div>
                          </DropdownToggle>

                          <DropdownMenu
                            end
                            className={schedulerStyles["office-dropdown-menu"]}
                          >
                            {isStaff && (
                              <DropdownItem
                                className={
                                  schedulerStyles["office-dropdown-item"]
                                }
                                onClick={() => {
                                  setShowOfficeSelectModal(true);
                                  setSelectedDateForLeave(dayMoment.toDate());
                                }}
                              >
                                {t("staffLeaves.applyLeave")}
                              </DropdownItem>
                            )}
                            <DropdownItem
                              className={
                                schedulerStyles["office-dropdown-item"]
                              }
                              onClick={() => {
                                history.push({
                                  pathname:
                                    constants.routes.scheduler.addBusySlot,
                                  state: {
                                    date: dayMoment.toDate(),
                                  },
                                });
                              }}
                            >
                              {t("accountOwner.addBusyslots")}
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>

            {/* Row 2: Holiday indicators */}
            {hasAnyHoliday && (
              <tr>
                <th className={styles.timeHeader}>
                  <div className={styles.empty_holi}></div>
                </th>
                {days.map((day, i) => {
                  const dateKey = day.date;
                  const dateObj = groupedAgendaByDate?.find(
                    (d) =>
                      moment(d.requestedDate).format("MMM DD, ddd") === dateKey,
                  );
                  const holidayList = dateObj?.holidayList || [];

                  return (
                    <th key={`holiday-${i}`} className={styles.holiday_header}>
                      {holidayList.length > 0 ? (
                        <div className={styles.holiday_container}>
                          <img src={HolidayImage} alt="holiday" />
                          <p className={styles.holiday_text}>
                            {holidayList[0]?.title || "HOLIDAY"}
                          </p>
                        </div>
                      ) : (
                        <div style={{ height: "32px" }}></div>
                      )}
                    </th>
                  );
                })}
              </tr>
            )}

            {/* All-day events row */}
            <tr>
              <th
                className={`${styles.timeHeader} ${styles.all_day_event_header}`}
              >
                <p className={styles.all_day_text}>all-day</p>
              </th>
              {days.map((day, dIndex) => {
                const dateKey = day.date;
                const dayMoment =
                  day.momentDate || moment(day.date, "MMM DD, ddd");
                const allDayEventsForDate = allDayEventsMap[dateKey] || {};
                const dateObj = groupedAgendaByDate?.find(
                  (d) =>
                    moment(d.requestedDate).format("MMM DD, ddd") === dateKey,
                );
                const holidayList = dateObj?.holidayList || [];
                const allEventsForDate =
                  Object.values(allDayEventsForDate).flat();
                const highlightCheck =
                  dateMatchForHighlight &&
                  dayMoment &&
                  moment(dateMatchForHighlight)
                    .startOf("day")
                    .isSame(dayMoment.startOf("day"));

                return (
                  <td
                    key={dIndex}
                    className={`${styles.eventCell} ${
                      holidayList.length > 0 ? styles.light_red_bg : ""
                    } ${highlightCheck ? "blinking-background" : ""}`}
                    onDragOver={(e) => handleDragOver(e, dIndex, 0, true)}
                    onDrop={(e) => handleDrop(e, dIndex, 0, true)}
                  >
                    {allEventsForDate.map((event, eIndex) => {
                      if (!event || !event.user || !event.item) return null;
                      const eventItem = event.item;
                      const createdByMe =
                        eventItem.createdById === currentUserId;
                      const isInvite = !!eventItem.eventEmployeeStatus;
                      const canDrag = canDragEvent(eventItem);
                      const isDragged =
                        isDragging &&
                        draggedEvent?.eventItem?.id === eventItem.id;

                      // Determine click behavior based on agenda type (matching AgendaWeeklyView)
                      const shouldRedirect =
                        eventItem.agendaType === constants.agendaType.EVENT ||
                        eventItem.agendaType ===
                          constants.agendaType.BUSY_SLOT ||
                        eventItem.agendaType === constants.agendaType.LEAVE;
                      const shouldShowModal =
                        eventItem.agendaType === constants.agendaType.BLOCKED ||
                        eventItem.agendaType ===
                          constants.agendaType.APPOINTMENT;

                      return (
                        <React.Fragment key={eIndex}>
                          <div
                            className={styles.eventBox}
                            draggable={canDrag}
                            onDragStart={(e) =>
                              handleDragStart(
                                e,
                                eventItem,
                                event,
                                day.momentDate,
                              )
                            }
                            onDragEnd={handleDragEnd}
                            style={{
                              backgroundColor: event.bgColor,
                              cursor: canDrag
                                ? "grab"
                                : shouldRedirect || shouldShowModal
                                  ? "pointer"
                                  : "default",
                              opacity: isDragged ? 0 : 1,
                              border: "1px solid rgba(0, 0, 0, 0.2)",
                            }}
                            onClick={(e) => {
                              if (isDragging) {
                                e.stopPropagation();
                                return;
                              }
                              const requestedDate = dateObj?.requestedDate;

                              if (shouldRedirect) {
                                // Events and BUSY_SLOT always redirect (matching AgendaWeeklyView)
                                goToDetails(
                                  eventItem.id,
                                  eventItem.agendaType,
                                  createdByMe,
                                  requestedDate,
                                  isInvite,
                                  eventItem,
                                );
                              } else if (shouldShowModal) {
                                // BLOCKED, APPOINTMENT, LEAVE show modal
                                setIsModal({
                                  open: true,
                                  type: eventItem.agendaType,
                                  officeId: eventItem?.officeId || null,
                                });
                              }
                            }}
                          >
                            {renderEventContent(event, event.dotColor)}
                          </div>
                        </React.Fragment>
                      );
                    })}
                    {/* Drag preview for all-day events */}
                    {isDragging &&
                      dragPreviewPosition &&
                      dragPreviewPosition.isAllDay &&
                      dragPreviewPosition.dayIndex === dIndex &&
                      draggedEvent &&
                      draggedEvent.eventItem && (
                        <div
                          style={{
                            backgroundColor: getEventColor(
                              draggedEvent.eventItem,
                            ),
                            border: `2px dashed ${getEventBorderColor(
                              draggedEvent?.eventItem,
                            )}`,
                            borderRadius: 4,
                            opacity: 0.6,
                            padding: "4px 8px",
                            marginTop: "4px",
                            pointerEvents: "none",
                            fontSize: "12px",
                          }}
                        >
                          {draggedEvent.fullEvent
                            ? renderEventContent(
                                draggedEvent.fullEvent,
                                draggedEvent.fullEvent?.dotColor || "#005f99",
                              )
                            : renderEventContent(
                                { item: draggedEvent?.eventItem },
                                "#005f99",
                              )}
                        </div>
                      )}
                  </td>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {/* Time-based events rows */}
            {hours.map((hour, hIndex) => (
              <tr key={hIndex}>
                <td className={styles.timeColumn}>
                  <div className={styles.date_container}>{hour.label}</div>
                </td>
                {days.map((day, dIndex) => {
                  const dateKey = day.date;
                  const dayMoment =
                    day.momentDate || moment(day.date, "MMM DD, ddd");
                  const dateObj = groupedAgendaByDate?.find(
                    (d) =>
                      moment(d.requestedDate).format("MMM DD, ddd") === dateKey,
                  );
                  const holidayList = dateObj?.holidayList || [];
                  const allTimeEvents = timeEventsByDate[dateKey] || [];
                  const highlightCheck =
                    dateMatchForHighlight &&
                    dayMoment &&
                    moment(dateMatchForHighlight)
                      .startOf("day")
                      .isSame(dayMoment.startOf("day"));

                  const hourStartMinutes = hIndex * 60;
                  const hourEndMinutes = (hIndex + 1) * 60;

                  const overlappingEvents = allTimeEvents.filter((event) => {
                    if (
                      !event ||
                      !event.item ||
                      !event.item.startTime ||
                      !event.item.endTime
                    ) {
                      return false;
                    }
                    const startMinutes = getTimeInMinutes(event.item.startTime);
                    const endMinutes = getTimeInMinutes(event.item.endTime);
                    return (
                      startMinutes < hourEndMinutes &&
                      endMinutes > hourStartMinutes
                    );
                  });

                  const eventMap = new Map();
                  overlappingEvents.forEach((fullEvent, idx) => {
                    if (!fullEvent || !fullEvent.item) return;
                    const item = fullEvent.item;
                    const uniqueKey = `${item.id || idx}-${
                      item.startTime || ""
                    }-${item.endTime || ""}-${fullEvent.user?.id || idx}`;
                    eventMap.set(uniqueKey, fullEvent);
                    const simpleKey = `${item.id || idx}-${
                      item.startTime || ""
                    }-${item.endTime || ""}`;
                    if (!eventMap.has(simpleKey)) {
                      eventMap.set(simpleKey, fullEvent);
                    }
                  });

                  const groupedEvents = groupEventsByStartTime(
                    overlappingEvents.map((e) => e.item).filter(Boolean),
                  );

                  return (
                    <td
                      key={dIndex}
                      className={`${styles.eventCell} ${
                        holidayList.length > 0 ? styles.light_red_bg : ""
                      } ${highlightCheck ? "blinking-background" : ""}`}
                      style={{ position: "relative", height: "60px" }}
                      onDragOver={(e) =>
                        handleDragOver(e, dIndex, hIndex, false)
                      }
                      onDrop={(e) => handleDrop(e, dIndex, hIndex, false)}
                    >
                      {Object.entries(groupedEvents).map(
                        ([startMinutesStr, group]) => {
                          const overlapPositions =
                            calculateOverlapPositions(group);

                          const usedEvents = new Set();

                          return overlapPositions.map(
                            (
                              {
                                event: eventItem,
                                widthFactor,
                                leftOffsetFactor,
                              },
                              index,
                            ) => {
                              let finalEvent = null;

                              for (const fullEvent of overlappingEvents) {
                                if (
                                  usedEvents.has(fullEvent) ||
                                  !fullEvent ||
                                  !fullEvent.item
                                )
                                  continue;

                                if (
                                  fullEvent.item.id === eventItem.id &&
                                  fullEvent.item.startTime ===
                                    eventItem.startTime &&
                                  fullEvent.item.endTime === eventItem.endTime
                                ) {
                                  finalEvent = fullEvent;
                                  usedEvents.add(fullEvent);
                                  break;
                                }
                              }

                              if (!finalEvent) {
                                for (const fullEvent of overlappingEvents) {
                                  if (
                                    usedEvents.has(fullEvent) ||
                                    !fullEvent ||
                                    !fullEvent.item
                                  )
                                    continue;

                                  if (
                                    fullEvent.item.id === eventItem.id &&
                                    fullEvent.item.startTime ===
                                      eventItem.startTime
                                  ) {
                                    finalEvent = fullEvent;
                                    usedEvents.add(fullEvent);
                                    break;
                                  }
                                }
                              }

                              if (!finalEvent || !finalEvent.user) return null;

                              const startMinutes = getTimeInMinutes(
                                eventItem.startTime,
                              );
                              const endMinutes = getTimeInMinutes(
                                eventItem.endTime,
                              );

                              const eventStartInHour = Math.max(
                                startMinutes,
                                hourStartMinutes,
                              );
                              const eventEndInHour = Math.min(
                                endMinutes,
                                hourEndMinutes,
                              );

                              const top =
                                ((eventStartInHour - hourStartMinutes) / 60) *
                                100;
                              let height =
                                ((eventEndInHour - eventStartInHour) / 60) *
                                100;

                              const HOUR_CELL_HEIGHT_PX = 60;
                              const MIN_EVENT_HEIGHT_PX = 8;
                              let eventHeightPx =
                                (height / 100) * HOUR_CELL_HEIGHT_PX;
                              let topPx = (top / 100) * HOUR_CELL_HEIGHT_PX;

                              if (eventHeightPx < MIN_EVENT_HEIGHT_PX) {
                                eventHeightPx = MIN_EVENT_HEIGHT_PX;
                                if (
                                  eventHeightPx + topPx >
                                  HOUR_CELL_HEIGHT_PX
                                ) {
                                  eventHeightPx = HOUR_CELL_HEIGHT_PX - topPx;
                                }
                              }
                              const heightPercent =
                                (eventHeightPx / HOUR_CELL_HEIGHT_PX) * 100;
                              const topPercent =
                                (topPx / HOUR_CELL_HEIGHT_PX) * 100;

                              const eventStartHour = Math.floor(
                                startMinutes / 60,
                              );
                              const isFirstHourOfEvent =
                                eventStartHour === hIndex;

                              const continuesNextHour =
                                endMinutes > hourEndMinutes;
                              const continuesFromPrevHour =
                                startMinutes < hourStartMinutes;

                              const eventColor = getEventColor(eventItem);

                              const createdByMe =
                                eventItem.createdById === currentUserId;
                              const isInvite = !!eventItem.eventEmployeeStatus;
                              const canDrag = canDragEvent(eventItem);
                              const isDragged =
                                isDragging &&
                                draggedEvent?.eventItem?.id === eventItem.id;

                              // Determine click behavior based on agenda type (matching AgendaWeeklyView)
                              const shouldRedirect =
                                eventItem.agendaType ===
                                  constants.agendaType.EVENT ||
                                eventItem.agendaType ===
                                  constants.agendaType.BUSY_SLOT ||
                                eventItem.agendaType ===
                                  constants.agendaType.LEAVE;
                              const shouldShowModal =
                                eventItem.agendaType ===
                                  constants.agendaType.BLOCKED ||
                                eventItem.agendaType ===
                                  constants.agendaType.APPOINTMENT;

                              const fullEventForDrag = finalEvent;

                              return (
                                <React.Fragment
                                  key={`${eventItem.id}-${index}`}
                                >
                                  <div
                                    draggable={canDrag}
                                    onDragStart={(e) =>
                                      handleDragStart(
                                        e,
                                        eventItem,
                                        fullEventForDrag,
                                        day.momentDate,
                                      )
                                    }
                                    onDragEnd={handleDragEnd}
                                    style={{
                                      position: "absolute",
                                      top: `${topPercent}%`,
                                      left: `${leftOffsetFactor * 100}%`,
                                      width: `${widthFactor * 100 - 1}%`,
                                      height: `${heightPercent}%`,
                                      backgroundColor: eventColor,
                                      borderRadius:
                                        continuesFromPrevHour ||
                                        continuesNextHour
                                          ? 0
                                          : 6,
                                      borderTopLeftRadius: continuesFromPrevHour
                                        ? 0
                                        : 6,
                                      borderTopRightRadius:
                                        continuesFromPrevHour ? 0 : 6,
                                      borderBottomLeftRadius: continuesNextHour
                                        ? 0
                                        : 6,
                                      borderBottomRightRadius: continuesNextHour
                                        ? 0
                                        : 6,
                                      zIndex: isDragged ? 1000 : 2,
                                      overflow: "hidden",
                                      cursor: canDrag
                                        ? "grab"
                                        : shouldRedirect || shouldShowModal
                                          ? "pointer"
                                          : "default",
                                      padding: "2px 4px",
                                      display: "flex",
                                      alignItems: "center",
                                      opacity: isDragged ? 0 : 1,
                                      borderLeft:
                                        "1px solid rgba(0, 0, 0, 0.2)",
                                      borderRight:
                                        "1px solid rgba(0, 0, 0, 0.2)",
                                      borderTop: continuesFromPrevHour
                                        ? "none"
                                        : "1px solid rgba(0, 0, 0, 0.2)",
                                      borderBottom: continuesNextHour
                                        ? "none"
                                        : "1px solid rgba(0, 0, 0, 0.2)",
                                    }}
                                    onClick={(e) => {
                                      if (isDragging) {
                                        e.stopPropagation();
                                        return;
                                      }
                                      const requestedDate =
                                        dateObj?.requestedDate;

                                      if (shouldRedirect) {
                                        // Events and BUSY_SLOT always redirect (matching AgendaWeeklyView)
                                        goToDetails(
                                          eventItem.id,
                                          eventItem.agendaType,
                                          createdByMe,
                                          requestedDate,
                                          isInvite,
                                          eventItem,
                                        );
                                      } else if (shouldShowModal) {
                                        // BLOCKED, APPOINTMENT, LEAVE show modal
                                        setIsModal({
                                          open: true,
                                          type: eventItem.agendaType,
                                          officeId: eventItem?.officeId || null,
                                        });
                                      }
                                    }}
                                  >
                                    {isFirstHourOfEvent && (
                                      <div
                                        style={{
                                          fontSize: 10,
                                          color: "#2D3245",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          width: "100%",
                                        }}
                                      >
                                        {renderEventContent(
                                          finalEvent,
                                          finalEvent.dotColor,
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </React.Fragment>
                              );
                            },
                          );
                        },
                      )}
                      {/* Drag preview for time-based events */}
                      {isDragging &&
                        dragPreviewPosition &&
                        !dragPreviewPosition.isAllDay &&
                        dragPreviewPosition.dayIndex === dIndex &&
                        dragPreviewPosition.hourIndex === hIndex &&
                        draggedEvent &&
                        draggedEvent.eventItem && (
                          <div
                            style={{
                              position: "absolute",
                              top: `${dragPreviewPosition.topPercent}%`,
                              left: 0,
                              width: "100%",
                              height: `${dragPreviewPosition.heightPercent}%`,
                              backgroundColor: getEventColor(
                                draggedEvent.eventItem,
                              ),
                              border: `2px dashed ${getEventBorderColor(
                                draggedEvent.eventItem,
                              )}`,
                              borderRadius: 6,
                              opacity: 0.6,
                              zIndex: 1000,
                              pointerEvents: "none",
                              display: "flex",
                              alignItems: "center",
                              padding: "2px 4px",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 10,
                                color: "#2D3245",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                width: "100%",
                              }}
                            >
                              {draggedEvent.fullEvent
                                ? renderEventContent(
                                    draggedEvent.fullEvent,
                                    draggedEvent.fullEvent?.dotColor ||
                                      "#005f99",
                                  )
                                : renderEventContent(
                                    { item: draggedEvent.eventItem },
                                    "#005f99",
                                  )}
                            </div>
                          </div>
                        )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <MoveToApp
          isModal={isModal?.open}
          openModal={setIsModal}
          type={isModal?.type}
          officeId={isModal?.officeId || null}
        />
      </div>
      {editModalConfirmation && pendingDragUpdate && (
        <EditOptionsModal
          isOpen={editModalConfirmation}
          closeStepOneModal={setEditModalConfirmation}
          loader={false}
          eventKey={
            pendingDragUpdate.isBusySlot
              ? {
                  eventOrShift: t("accountOwner.busySlot").toLowerCase(),
                }
              : {
                  eventOrShift: t(
                    `accountOwner.${
                      pendingDragUpdate.eventData?.isShift ? "shift" : "event"
                    }Tab`,
                  ).toLowerCase(),
                }
          }
          eventOrShiftTab={
            pendingDragUpdate.isBusySlot
              ? false
              : pendingDragUpdate.eventData?.isShift || false
          }
          busySlots={pendingDragUpdate.isBusySlot || false}
          repeatedOptionChanged={pendingDragUpdate.anyChangedForDate || false}
          handleConfirm={async (value) => {
            try {
              if (pendingDragUpdate.isBusySlot) {
                await performBusySlotUpdate(
                  pendingDragUpdate.busySlotData,
                  pendingDragUpdate.newDateTime,
                  value,
                  pendingDragUpdate.officeDetails,
                  pendingDragUpdate.officeIds,
                  pendingDragUpdate.busySlotEmployeeView,
                  pendingDragUpdate.isAllDayBusy,
                  pendingDragUpdate.repeatedType,
                  pendingDragUpdate.repeatedEndDate,
                  pendingDragUpdate.originalRequestedDate,
                  pendingDragUpdate.UpdateFlags,
                );
              } else {
                await performEventUpdate(
                  pendingDragUpdate.eventData,
                  pendingDragUpdate.newDateTime,
                  value,
                  pendingDragUpdate.originalRequestedDate,
                );
              }
              setEditModalConfirmation(false);
              setPendingDragUpdate(null);
            } catch (error) {
              // Error already shown in perform functions
              setEditModalConfirmation(false);
              setPendingDragUpdate(null);
            }
          }}
        />
      )}
      {showOfficeSelectModal && (
        <SelectOfficeModal
          selectedOwnerId={selectedOwnerId}
          showOfficeSelectModal={showOfficeSelectModal}
          setShowOfficeSelectModal={setShowOfficeSelectModal}
          SelectedDateForLeave={SelectedDateForLeave}
        />
      )}
    </>
  );
}

export default withTranslation()(WeeklyListView);
